import React, { createContext, SetStateAction, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { env } from "env";

import toast from "components/partials/toast/toast";

import { getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { useCurrentUser } from "hooks/api/use-current-user";
import { commentKeys } from "hooks/api/query-keys";
import type { TouchpointCommentScope } from "hooks/api/use-touchpoint-comments";

import { Permission } from "types/auth";
import {
  CommentsSummary,
  CommentFilterOption,
  CommentReply,
  commentFilterOptions,
  NewComment,
  Comment,
  emptyCommentSummary,
} from "types/comments";
import {
  DEFAULT_PAGINATED_REQUEST_OPTIONS,
  emptyPaginatedResponse,
  PaginatedResponse,
  PaginatedRequestOptions,
} from "types/pagination";

export interface CommentContextState {
  commentData: PaginatedResponse<Comment>;
  setCommentData: React.Dispatch<SetStateAction<PaginatedResponse<Comment>>>;
  commentsSummary: CommentsSummary;
  commentFilter: CommentFilterOption;
  setCommentFilter: React.Dispatch<SetStateAction<CommentFilterOption>>;
  loadComments: (options?: PaginatedRequestOptions) => Promise<PaginatedResponse<Comment>>;
  loadCommentsSummary: () => Promise<CommentsSummary>;
  submitNewComment: (comment: NewComment) => Promise<Comment | void>;
  updateComment: (comment: Comment) => Promise<Comment | void>;
  loadCommentReplies: (comment: Comment) => Promise<PaginatedResponse<CommentReply>>;
  submitNewCommentReply: (newComment: NewComment) => Promise<CommentReply | void>;
  lastActiveCommentId: string | undefined;
  setLastActiveCommentId: React.Dispatch<SetStateAction<string | undefined>>;
  isFABOpen: boolean;
  setIsFABOpen: React.Dispatch<SetStateAction<boolean>>;
  openCommentAnnotationId: string | undefined;
  setOpenCommentAnnotationId: (id: string | undefined) => void;
  newCommentModuleId: string | undefined;
  setNewCommentModuleId: (id: string | undefined) => void;
  isClientUser: boolean;
  commentingDisabled: boolean;
}
const initialContextState: CommentContextState = {
  commentData: emptyPaginatedResponse,
  setCommentData: () => {},
  commentsSummary: emptyCommentSummary,
  commentFilter: commentFilterOptions[0],
  setCommentFilter: () => {},
  loadComments: () => Promise.reject("function called before instantiated in context"),
  loadCommentsSummary: () => Promise.reject("function called before instantiated in context"),
  submitNewComment: () => Promise.reject("function called before instantiated in context"),
  updateComment: () => Promise.reject("function called before instantiated in context"),
  loadCommentReplies: () => Promise.reject("function called before instantiated in context"),
  submitNewCommentReply: () => Promise.reject("function called before instantiated in context"),
  lastActiveCommentId: undefined,
  setLastActiveCommentId: () => {},
  isFABOpen: false,
  setIsFABOpen: () => {},
  openCommentAnnotationId: undefined,
  setOpenCommentAnnotationId: () => {},
  newCommentModuleId: undefined,
  setNewCommentModuleId: () => {},
  isClientUser: false,
  commentingDisabled: false,
};

const CommentContext: React.Context<CommentContextState> = createContext(initialContextState);
export default CommentContext;

interface CommentContextProps {
  loadComments: (options?: PaginatedRequestOptions) => Promise<PaginatedResponse<Comment>>;
  loadCommentsSummary: () => Promise<CommentsSummary>;
  submitNewComment: (comment: NewComment) => Promise<Comment>;
  updateComment: (comment: Comment) => Promise<Comment>;
  loadCommentReplies: (comment: Comment) => Promise<PaginatedResponse<CommentReply>>;
  submitNewCommentReply: (newComment: NewComment) => Promise<CommentReply>;
  commentScope: TouchpointCommentScope;
  commentingDisabled?: boolean;
  children?: React.ReactNode;
}

export const CommentContextProvider = ({
  loadComments,
  loadCommentsSummary,
  submitNewComment,
  updateComment,
  loadCommentReplies,
  submitNewCommentReply,
  commentScope,
  commentingDisabled = false,
  children,
}: CommentContextProps) => {
  const [commentData, setCommentData] =
    useState<PaginatedResponse<Comment>>(emptyPaginatedResponse);

  const { data: commentsSummary = emptyCommentSummary, refetch: refetchCommentsSummary } = useQuery(
    {
      queryKey: commentKeys.summary(
        commentScope.clientId,
        commentScope.campaignId,
        commentScope.touchpointId,
      ),
      queryFn: () => loadCommentsSummary(),
      enabled: Boolean(
        commentScope.clientId && commentScope.campaignId && commentScope.touchpointId,
      ),
    },
  );

  const [commentFilter, setCommentFilter] = useState<CommentFilterOption>(commentFilterOptions[0]);
  const [lastActiveCommentId, setLastActiveCommentId] = useState<string>();
  // Each commentContext should have one FAB at a time
  const [isFABOpen, setIsFABOpen] = useState<boolean>(false);
  const [openCommentAnnotationId, setOpenCommentAnnotationId] = useState<string | undefined>();
  const [newCommentModuleId, setNewCommentModuleId] = useState<string | undefined>();
  const currentUser = useCurrentUser();
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  useEffect(() => {
    handleLoadComments();
    // eslint-disable-next-line
  }, [commentFilter]);

  function appendBadgeNumber(comment: Comment, currentCount = 0) {
    if (comment.annotationId) {
      comment.badgeNumber = (currentCount + 1).toString();
    }
    return comment;
  }

  const handleLoadComments = (options?: PaginatedRequestOptions) => {
    return loadComments({
      ...DEFAULT_PAGINATED_REQUEST_OPTIONS,
      ...options,
      filter: commentFilter.value,
    }).then((response) => {
      setCommentData((prevComments) => {
        const isFirstPage = response.page === 0;

        const totalElements = response.totalItems;
        const startingBadgeNumber = totalElements - response.page * response.size;

        const comments: PaginatedResponse<Comment> = {
          ...response,
          items: response.items.map((comment, index) =>
            appendBadgeNumber(comment, startingBadgeNumber - index - 1),
          ),
        };

        if (isFirstPage) {
          return comments;
        }
        return {
          ...comments,
          items: [...prevComments.items, ...comments.items],
        };
      });
      return response;
    });
  };

  const handleLoadCommentsSummary = () =>
    refetchCommentsSummary().then((result) => result.data ?? emptyCommentSummary);

  const handleSubmitNewComment = (comment: NewComment) => {
    return submitNewComment(comment)
      .then((newComment) => {
        setCommentData((comments) => {
          const totalElements = comments.totalItems;
          const newBadgeNumber = totalElements + 1;

          return {
            ...comments,
            totalItems: newBadgeNumber,
            items: [appendBadgeNumber(newComment, newBadgeNumber - 1), ...comments.items],
          };
        });
        setLastActiveCommentId(newComment.id);
        handleLoadCommentsSummary();
        return newComment;
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to post comment",
            message: getErrorMessage(err),
          },
        });
      });
  };

  /**
   * Append a reply to an existing comment */
  const handleUpdateComment = (comment: Comment) => {
    return updateComment(comment)
      .then((updatedComment) => {
        const newCommentData = [...commentData.items];
        const foundIndex = newCommentData.findIndex((c) => c.id === updatedComment.id);

        // Updated comment retains original badge number
        updatedComment.badgeNumber = comment.badgeNumber;

        if (foundIndex >= 0) {
          newCommentData[foundIndex] = updatedComment;
          setCommentData((prevData) => {
            return { ...prevData, items: newCommentData };
          });
        } else {
          setCommentData((prevData) => {
            return { ...prevData, items: [...newCommentData, updatedComment] };
          });
        }
        setLastActiveCommentId(updatedComment.id);
        handleLoadCommentsSummary();
        return updatedComment;
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to update comment",
            message: getErrorMessage(err),
          },
        });
      });
  };

  const handleSubmitNewCommentReply = (newComment: NewComment) => {
    return submitNewCommentReply(newComment)
      .then((res) => {
        const newCommentData = [...commentData.items];
        newCommentData.forEach((c, i) => {
          if (c.id === newComment.parentId) {
            newCommentData[i].replyCount++;
          }
        });
        setCommentData((prevData) => {
          return { ...prevData, items: newCommentData };
        });
        setLastActiveCommentId(newComment.parentId);
        return res;
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to submit reply",
            message: getErrorMessage(err),
          },
        });
      });
  };

  const handleLoadCommentReplies = (comment: Comment) => {
    setLastActiveCommentId(comment.id);
    return loadCommentReplies(comment).then((res) => {
      return res;
    });
  };

  const CommentContextState: CommentContextState = {
    commentData,
    setCommentData,
    commentsSummary,
    commentFilter,
    setCommentFilter,
    lastActiveCommentId,
    setLastActiveCommentId,
    isFABOpen,
    setIsFABOpen,
    newCommentModuleId,
    setNewCommentModuleId,
    openCommentAnnotationId,
    loadComments: handleLoadComments,
    loadCommentsSummary: handleLoadCommentsSummary,
    submitNewComment: handleSubmitNewComment,
    updateComment: handleUpdateComment,
    loadCommentReplies: handleLoadCommentReplies,
    submitNewCommentReply: handleSubmitNewCommentReply,
    setOpenCommentAnnotationId,
    isClientUser,
    commentingDisabled,
  };

  if (env.MODE === "development") {
    (window as any).CommentContextState = CommentContextState;
  }

  return <CommentContext.Provider value={CommentContextState}>{children}</CommentContext.Provider>;
};
